import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { FiltersValue } from '../components/Filters';
import {
	buildAvailabilitySearch,
	parseFiltersFromSearch,
	parsePageFromSearch,
	parsePerPageFromSearch,
} from '../utils/availabilityQueryParams';

function sameStringArray(a: string[], b: string[]): boolean {
	return a.join(',') === b.join(',');
}

export interface AvailabilityLabelSource {
	id: string;
	atc: string;
}

// Owns the Availability filter/pagination state and keeps it in sync with the URL.
// - Writes state changes back to the URL (shareable, refresh-safe).
// - Reads the URL on external navigation (browser back/forward), skipping updates that
//   already match the current state so resolved chip labels are not clobbered.
export function useAvailabilityFilters() {
	const location = useLocation();
	const history = useHistory();

	const [filters, setFilters] = useState<FiltersValue>(() => parseFiltersFromSearch(location.search));
	const [page, setPage] = useState(() => parsePageFromSearch(location.search));
	const [perPage, setPerPage] = useState(() => parsePerPageFromSearch(location.search));

	useEffect(() => {
		const urlFilters = parseFiltersFromSearch(location.search);
		const urlPage = parsePageFromSearch(location.search);
		const urlPerPage = parsePerPageFromSearch(location.search);

		const matchesState =
			urlPage === page &&
			urlPerPage === perPage &&
			sameStringArray(
				urlFilters.items.map((item) => item.id),
				filters.items.map((item) => item.id)
			) &&
			sameStringArray(urlFilters.rs, filters.rs);

		if (matchesState) {
			return;
		}

		setFilters(urlFilters);
		setPage(urlPage);
		setPerPage(urlPerPage);
	}, [location.search, page, perPage, filters]);

	const pushSearch = (nextFilters: FiltersValue, nextPage: number, nextPerPage: number) => {
		history.push({
			pathname: location.pathname,
			search: buildAvailabilitySearch(location.search, nextFilters, nextPage, nextPerPage),
		});
	};

	const changeFilters = (value: FiltersValue) => {
		setFilters(value);
		setPage(1);
		pushSearch(value, 1, perPage);
	};

	const changePage = (nextPage: number) => {
		setPage(nextPage);
		pushSearch(filters, nextPage, perPage);
	};

	const changePerPage = (nextPerPage: number) => {
		setPerPage(nextPerPage);
		setPage(1);
		pushSearch(filters, 1, nextPerPage);
	};

	// The URL only stores item ids, so filters parsed from the URL initially have their label set
	// to the id itself. Once the matching groups are loaded, resolve the real ATC label so the
	// selected chips don't show a raw uuid to the user.
	const resolveLabels = (groups: AvailabilityLabelSource[]) => {
		setFilters((prev) => {
			if (prev.items.length === 0) {
				return prev;
			}

			let changed = false;
			const items = prev.items.map((item) => {
				if (item.label !== item.id) {
					return item;
				}

				const match = groups.find((group) => group.id === item.id);
				if (!match) {
					return item;
				}

				changed = true;
				return { id: item.id, label: match.atc };
			});

			return changed ? { ...prev, items } : prev;
		});
	};

	return { filters, page, perPage, changeFilters, changePage, changePerPage, resolveLabels };
}
